import { Button, getAppointmentTypeContext } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { Blac } from "blac-next";
import { DisableAppPadding } from "components/AppRouter/AppRouter";
import Translate from "components/Translate/Translate";
import React, { useCallback, useEffect } from "react";
import { APP_BREAKPOINT, APP_CONTENT_WIDTH } from "src/constants/layout";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import {
  taskManagementState,
  toast,
  tracker,
  userState
} from "src/state/state";
import { ProfileAttributesKeys } from "src/state/UserCubit/UserCubit";
import HomePageBgAllInOne from "src/ui/assets/images/home-page-bg-all-in-one.webp";
import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";

const OnboardingPageWrap = styled.div`
  label: OnboardingPageWrap;
  display: grid;
  place-items: center;
  height: 100%;
  width: 100%;
  position: relative;
  padding-bottom: calc(
    var(--ion-safe-area-bottom, 0) + var(--navbar-padding-bottom, 55px) + 1rem
  );
  padding-top: calc(var(--safe-area-top, 0) + 2rem);
  overflow: auto;
  background: var(--onboarding-background, transparent);

  > div {
    height: auto;
  }
`;

const OnboardingStepsWrapper = styled.div`
  label: OnboardingStepsWrapper;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  text-align: center;
  z-index: 2;
  gap: 2rem;
  justify-content: space-between;
  max-width: ${APP_CONTENT_WIDTH}px;
  padding: 0 1.5rem 1rem;
  height: auto;
`;

const BackgroundImage = styled.img`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  opacity: var(--onboarding-background-opacity, 1);
`;

const OnboardingTitle = styled.h2`
  font-size: 2.4rem;
  margin-bottom: 0.75rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    font-size: 2.5rem;
  }
`;

const OnboardingDescription = styled.p`
  font-size: 1.125rem;
  margin-bottom: 2rem;
  text-wrap: balance;
`;

const OnboardingScreenVisitOnly = () => {
  const onboardinbBloc = Blac.getBloc(OnboardingBloc);
  const task = onboardinbBloc.preOnboardingSyncVisitTask;
  const appointmentType = task?.additionalData?.appointment_type as
    | string
    | undefined;

  if (!appointmentType) {
    reportErrorSentry(
      new Error("Appointment type is not available in the task data")
    );
    toast.error("error.generic");
    return null;
  }

  const getTrackingProperties = useCallback(() => {
    const { profileAttributes } = userState.state;
    const payer =
      profileAttributes?.[ProfileAttributesKeys.partnerPayersCurrent];
    const employer =
      profileAttributes?.[ProfileAttributesKeys.partnerEmployersCurrent];

    const client_name = payer?.active
      ? payer.partner.name
      : employer?.active
        ? employer.partner.name
        : "Direct";

    return {
      client_name,
      visit_type: appointmentType,
      copay_applies: false
    };
  }, [appointmentType]);

  useEffect(() => {
    tracker.track("VFO Schedule Screen Viewed", {
      data: getTrackingProperties()
    });
  }, [getTrackingProperties]);

  const handleSchedulePress = useCallback(() => {
    tracker.track("VFO Schedule Now Clicked", {
      data: getTrackingProperties()
    });

    AppQueryPopupsController.openPopup(AppPopup.scheduleAppointment, {
      additionalParameters: {
        type: appointmentType,
        stay: "false"
      },
      onEvent: {
        popupClosed: () => {
          // Fallback mechanism: Refresh onboarding tasks after appointment scheduling popup closes
          // This ensures the PRE_COMPLETE_ONBOARDING task status is updated even if WebSocket fails
          setTimeout(() => {
            void taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
          }, 1000); // 1 second delay to allow backend processing
        }
      }
    });
  }, [appointmentType, getTrackingProperties]);

  const handleReadMorePress = useCallback(() => {
    tracker.track("VFO Learn More FAQ Clicked", {
      data: getTrackingProperties()
    });

    AppQueryPopupsController.openPopup(AppPopup.faq, {
      additionalParameters: {
        context: "visitOnlyOnboarding",
        stay: "false"
      }
    });
  }, [getTrackingProperties]);

  return (
    <OnboardingPageWrap>
      <div>
        <DisableAppPadding />
        <BackgroundImage src={HomePageBgAllInOne} />

        <OnboardingStepsWrapper>
          <div>
            <OnboardingTitle>
              <Translate
                msg="onboarding.vfo.title"
                variables={{
                  name: translate("appointment.name", {
                    context: getAppointmentTypeContext(appointmentType)
                  })
                }}
              />
            </OnboardingTitle>

            <OnboardingDescription>
              <Translate msg="onboarding.vfo.subtitle" />
            </OnboardingDescription>
          </div>
          <div>
            <Button
              center
              theme="dusk"
              onPress={() => {
                handleSchedulePress();
              }}
            >
              <Translate msg={"onboarding.vfo.scheduleLinkText"} />
            </Button>
            <Button
              center
              style={{ marginTop: "0.3rem" }}
              theme="transparent"
              hideArrow
              onPress={() => {
                handleReadMorePress();
              }}
            >
              <Translate msg={"onboarding.vfo.learnMoreLinkText"} />
            </Button>
          </div>
        </OnboardingStepsWrapper>
      </div>
    </OnboardingPageWrap>
  );
};

export default OnboardingScreenVisitOnly;
